import * as Dialog from '@radix-ui/react-dialog'
import { useMemo, useState } from 'react'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import type { ApiError } from '../../utils/api-client/api-error.ts'
import {
  UNGROUPED_PROJECT_FILTER,
  useAddProjectMemberships,
  useGetRepositories,
  usePostProject,
} from '../../utils/api-client/api-hooks.ts'
import type { ProjectResponse } from '../../utils/api-client/api-schemas.ts'
import { DefaultButton } from '../buttons/default-button.tsx'
import { GhostButton } from '../buttons/ghost-button.tsx'
import { SearchInput } from '../input/search-input.tsx'
import { TextInput } from '../input/text-input.tsx'
import { ADMIN_REQUIRED_OVERRIDE } from './admin-required-override.ts'
import { toPickableRepository } from './pickable-repositories.ts'
import * as styles from './project-dialog.css.ts'
import { RepositoryPickList } from './repository-pick-list.tsx'
import { SelectionSummary } from './selection-summary.tsx'
import { SELECTION_CAP_REASON, useRepositoryPicker } from './use-repository-picker.ts'

/** The problem type `POST /projects` and `PUT /projects/{id}` raise for a name already taken. */
export const DUPLICATE_PROJECT_PROBLEM_TYPE = 'pixee:problems/api/projects/duplicate-name'

/** The pick list is bounded by what the API can page; a create dialog offers the ungrouped tail. */
const CANDIDATE_PAGE_SIZE = 1000

export type CreateProjectDialogProps = {
  onClose: () => unknown
  onCreated: (project: ProjectResponse) => unknown
}

const isDuplicateName = (error: ApiError) =>
  error.status === 409 && (error.problemType === undefined || error.problemType === DUPLICATE_PROJECT_PROBLEM_TYPE)

/**
 * Names a project, optionally describes it, and picks the ungrouped repositories to start it with.
 * The prompt pre-selects candidates; nothing is assigned until the project exists.
 */
export const CreateProjectDialog: React.FC<CreateProjectDialogProps> = ({ onClose, onCreated }) => {
  const { addToast } = useToastService()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [nameError, setNameError] = useState<string>()

  const { data: ungrouped } = useGetRepositories({
    pageIndex: 0,
    pageSize: CANDIDATE_PAGE_SIZE,
    projectIds: [UNGROUPED_PROJECT_FILTER],
  })
  const candidates = useMemo(() => (ungrouped?._embedded.items ?? []).map(toPickableRepository), [ungrouped])
  const notOffered = Math.max(0, (ungrouped?.total ?? 0) - candidates.length)
  const picker = useRepositoryPicker({ candidates })

  const showAssignError = useShowMutationError({
    prefix: 'Project created, but adding repositories failed',
    overrides: ADMIN_REQUIRED_OVERRIDE,
  })
  const assignMutation = useAddProjectMemberships({ onError: showAssignError })

  const showCreateError = useShowMutationError({
    prefix: 'Failed to create project',
    overrides: ADMIN_REQUIRED_OVERRIDE,
  })
  const createMutation = usePostProject({
    onSuccess: project => {
      const repositoryIds = [...picker.pickedIds]
      const finish = () => {
        addToast({ message: <>Project &ldquo;{project.name}&rdquo; created</>, variant: 'success' })
        onCreated(project)
      }
      if (repositoryIds.length === 0) {
        finish()
        return
      }
      assignMutation.mutate({ projectId: project.id, repositoryIds }, { onSuccess: finish, onError: () => finish() })
    },
    onError: (error, ...rest) => {
      if (isDuplicateName(error)) {
        setNameError(`A project named ${name.trim()} already exists.`)
        return
      }
      showCreateError(error, ...rest)
    },
  })

  const isPending = createMutation.isPending || assignMutation.isPending
  const trimmedName = name.trim()
  const trimmedDescription = description.trim()

  const handleCreate = () => {
    if (trimmedName === '') {
      return
    }
    setNameError(undefined)
    createMutation.mutate({
      body: {
        name: trimmedName,
        description: trimmedDescription === '' ? undefined : trimmedDescription,
      },
    })
  }

  return (
    <Dialog.Root
      open
      onOpenChange={open => {
        if (!open && !isPending) {
          onClose()
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content} onPointerDownOutside={event => event.preventDefault()}>
          <Dialog.Title className={styles.title}>Create project</Dialog.Title>
          <Dialog.Description hidden>Name the project and pick the repositories it starts with.</Dialog.Description>

          <TextInput
            size="small"
            placeholder="Project name"
            ariaLabel="Project name"
            value={name}
            onChange={value => {
              setName(value)
              setNameError(undefined)
            }}
            isError={nameError !== undefined}
            errorText={nameError}
            isRequired
          />
          <TextInput
            size="small"
            placeholder="Short description (optional)"
            ariaLabel="Short description"
            value={description}
            onChange={setDescription}
          />

          <SearchInput value={picker.filter} onChange={picker.setFilter} size="small" name="Search repositories" />

          {picker.shownCandidates.length > 0 ? (
            <RepositoryPickList
              label={picker.isFiltering ? 'Matching repositories' : 'Ungrouped repositories'}
              candidates={picker.shownCandidates}
              pickedIds={picker.pickedIds}
              onToggle={picker.toggle}
              unpickedDisabledReason={picker.isAtSelectionCap ? SELECTION_CAP_REASON : undefined}
            />
          ) : picker.isFiltering ? (
            <p className={styles.emptyNote}>No repositories match &ldquo;{picker.filter.trim()}&rdquo;.</p>
          ) : (
            <p className={styles.emptyNote}>Every repository already belongs to a project.</p>
          )}
          {notOffered > 0 && (
            <p className={styles.emptyNote}>
              Showing the first {candidates.length} ungrouped repositories; {notOffered} more can be added from the
              project once it exists.
            </p>
          )}

          <div className={styles.bar}>
            <SelectionSummary selectedCount={picker.pickedIds.size} onClear={picker.clearPicked} />
            <span className={styles.buttons}>
              <GhostButton
                type="secondary"
                className={styles.outlinedGhost}
                onClick={onClose}
                state={isPending ? 'disabled' : 'default'}
              >
                Cancel
              </GhostButton>
              <DefaultButton
                state={isPending ? 'loading' : trimmedName === '' ? 'disabled' : 'default'}
                onClick={handleCreate}
                loadingLabel="Creating project"
              >
                Create project
              </DefaultButton>
            </span>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
